import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Search, Crosshair, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PIN = L.divIcon({
  className: "",
  html: '<div style="width:22px;height:22px;background:#10B981;border:3px solid #062217;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,.5)"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 22],
});

export default function LocationPicker({ lat, lng, onChange }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const hasLoc = lat != null && lng != null;

  useEffect(() => {
    if (mapRef.current || !elRef.current) return;
    const center = hasLoc ? [lat, lng] : [9.082, 8.6753];
    const map = L.map(elRef.current).setView(center, hasLoc ? 16 : 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19, attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);
    const marker = L.marker(center, { draggable: true, icon: PIN }).addTo(map);
    marker.on("dragend", () => {
      const p = marker.getLatLng();
      onChange(+p.lat.toFixed(6), +p.lng.toFixed(6));
    });
    map.on("click", (e) => {
      marker.setLatLng(e.latlng);
      onChange(+e.latlng.lat.toFixed(6), +e.latlng.lng.toFixed(6));
    });
    mapRef.current = map;
    markerRef.current = marker;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; markerRef.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapRef.current || !hasLoc) return;
    markerRef.current.setLatLng([lat, lng]);
    mapRef.current.setView([lat, lng], Math.max(mapRef.current.getZoom(), 16));
  }, [lat, lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = async () => {
    if (q.trim().length < 3) { toast.error("Type at least 3 characters to search."); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/workplaces/geocode", { params: { q } });
      setResults(data);
      if (!data.length) toast.error("No matches found. Try a more specific address.");
    } catch {
      toast.error("Address search is unavailable right now. Use the map or 'Use my location'.");
    } finally { setBusy(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Your device doesn't support location."); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setBusy(false); onChange(+p.coords.latitude.toFixed(6), +p.coords.longitude.toFixed(6)); toast.success("Pinned your current location."); },
      () => { setBusy(false); toast.error("Couldn't get your location. Allow location access and try again."); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className="space-y-2" data-testid="location-picker">
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
          placeholder="Search an address or place name" className="bg-[#0A0D0C] border-emerald-500/20" data-testid="location-search-input" />
        <Button type="button" onClick={search} disabled={busy} variant="outline" className="border-emerald-500/40 shrink-0" data-testid="location-search-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
        <Button type="button" onClick={useMyLocation} disabled={busy} variant="outline" className="border-emerald-500/40 shrink-0" title="Use my current location" data-testid="use-my-location-btn">
          <Crosshair className="w-4 h-4" />
        </Button>
      </div>
      {results.length > 0 && (
        <div className="rounded-lg border border-emerald-500/15 bg-[#0A0D0C] max-h-36 overflow-y-auto divide-y divide-white/5" data-testid="location-results">
          {results.map((r, i) => (
            <button type="button" key={i} onClick={() => { onChange(r.lat, r.lng); setResults([]); setQ(r.display_name); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-white/5" data-testid={`location-result-${i}`}>{r.display_name}</button>
          ))}
        </div>
      )}
      <div ref={elRef} className="h-56 w-full rounded-xl overflow-hidden border border-emerald-500/20 z-0" data-testid="location-map" />
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <MapPin className="w-3 h-3" />
        {hasLoc ? <>Pinned at {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)} — drag the pin to fine-tune.</> : "No location set. Search an address, tap the map, or use your current location."}
      </p>
    </div>
  );
}
